import { useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import type { ApprovalStatus, PublishedPost } from "../lib/supabase";
import { fmtRelative, laneClass, laneLabel } from "../lib/format";
import { useSupabaseQuery } from "../hooks/useSupabaseQuery";

type Filter = "all" | ApprovalStatus;

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all",      label: "All" },
  { key: "pending",  label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

const PAGE_SIZE = 50;

export default function PostsFeed() {
  const [filter, setFilter] = useState<Filter>("pending");
  const [search, setSearch] = useState("");

  const { data, error, loading, refetch } = useSupabaseQuery(
    async () => {
      let q = supabase
        .from("published_posts")
        .select("*")
        .order("synth_at", { ascending: false, nullsFirst: false })
        .limit(PAGE_SIZE);
      if (filter !== "all") q = q.eq("approval_status", filter);
      return q as unknown as Promise<{ data: PublishedPost[] | null; error: any }>;
    },
    [filter],
    30_000,
  );

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data;
    const needle = search.toLowerCase();
    return data.filter(
      (p) =>
        (p.title || "").toLowerCase().includes(needle) ||
        (p.content_preview || "").toLowerCase().includes(needle),
    );
  }, [data, search]);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0 };
    for (const p of data || []) {
      if (p.approval_status in c) c[p.approval_status as keyof typeof c]++;
    }
    return c;
  }, [data]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`btn ${filter === f.key ? "btn-ghost border-chrome-300 text-chrome-100" : "btn-ghost"}`}
            >
              {f.label}
              {f.key !== "all" && (
                <span className="ml-2 text-chrome-400 font-mono">{counts[f.key]}</span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title / preview…"
            className="bg-ink-900 border border-ink-700 rounded px-3 py-1.5 text-sm w-64 focus:outline-none focus:border-lane2"
          />
          <button onClick={refetch} className="btn btn-ghost">↻</button>
        </div>
      </div>

      {loading && !data && (
        <div className="text-sm text-chrome-400">Loading…</div>
      )}
      {error && (
        <div className="card border-bad/40 bg-bad/5">
          <p className="text-sm text-bad">{(error as Error).message || "Query failed"}</p>
        </div>
      )}
      {data && filtered.length === 0 && (
        <div className="card text-sm text-chrome-400">No posts match.</div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {filtered.map((post) => (
          <PostCard key={post.id} post={post} onChanged={refetch} />
        ))}
      </div>
    </div>
  );
}

interface PostCardProps {
  post: PublishedPost;
  onChanged: () => void;
}

function PostCard({ post, onChanged }: PostCardProps) {
  const [busy, setBusy] = useState<ApprovalStatus | null>(null);
  const [localStatus, setLocalStatus] = useState<ApprovalStatus>(post.approval_status);

  const setStatus = async (next: ApprovalStatus) => {
    setBusy(next);
    const { error } = await supabase
      .from("published_posts")
      .update({ approval_status: next, reviewed_at: new Date().toISOString() })
      .eq("id", post.id);
    setBusy(null);
    if (error) {
      alert(`Update failed: ${error.message}`);
      return;
    }
    setLocalStatus(next);
    onChanged();
  };

  return (
    <article className="card hover:border-ink-600 transition">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`chip ${laneClass(post.lane)}`}>{laneLabel(post.lane)}</span>
            {post.style && <span className="chip chip-pending">{post.style}</span>}
            {post.tier && <span className="chip chip-pending">{post.tier}</span>}
            <span className={`chip chip-${localStatus}`}>{localStatus}</span>
            <span className="text-xs text-chrome-400 font-mono ml-auto">
              {fmtRelative(post.synth_at)}
            </span>
          </div>
          <h3 className="text-sm font-medium text-chrome-100 line-clamp-2">
            {post.title || <span className="text-chrome-400 italic">no title</span>}
          </h3>
          {post.content_preview && (
            <p className="mt-1 text-sm text-chrome-300 line-clamp-3 whitespace-pre-line">
              {post.content_preview}
            </p>
          )}
          {post.source_url && (
            <a
              href={post.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-xs font-mono text-lane2 hover:underline truncate max-w-full"
            >
              {post.source_url}
            </a>
          )}
        </div>
        <div className="shrink-0 flex flex-col gap-1.5">
          <button
            disabled={busy !== null || localStatus === "approved"}
            onClick={() => setStatus("approved")}
            className="btn btn-approve disabled:opacity-40"
          >
            {busy === "approved" ? "…" : "Approve"}
          </button>
          <button
            disabled={busy !== null || localStatus === "rejected"}
            onClick={() => setStatus("rejected")}
            className="btn btn-reject disabled:opacity-40"
          >
            {busy === "rejected" ? "…" : "Reject"}
          </button>
          {localStatus !== "pending" && (
            <button
              disabled={busy !== null}
              onClick={() => setStatus("pending")}
              className="btn btn-ghost disabled:opacity-40"
            >
              Reset
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
